import React, { useEffect, useState } from 'react';
import { Dialog, Input, Message } from '@fx-ui/fine-design';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import { get, isEmpty, size, trim, ECode } from '@libs/shared';
import { getRequestUrl, getErrorMsg, post, hasFunctionContactPhone } from '../../../../utils';
import { authModel } from '../../../../models';
import { platformIntl, API, UGC_MAX_LENGTH } from '../../../../constants';

interface IEditPaneProps {
    value?: any;
    visible?: boolean;
    onCancel?: () => void;
    onOk?: (val: any) => void;
}

const { TARGET_NAME, TARGET_EMAIL } = UGC_MAX_LENGTH.MSG;

const MsgTargetEditPane: React.FC<IEditPaneProps> = (props) => {
    const { value, visible, onCancel, onOk } = props;
    const [name, setName] = useState('');
    const [nameState, setNameState] = useState<'error'>();
    const [phone, setPhone] = useState('');
    const [phoneState, setPhoneState] = useState<'error'>();
    const [email, setEmail] = useState('');
    const [emailState, setEmailState] = useState<'error'>();

    useEffect(() => {
        if (visible) {
            if (value) {
                setName(value.name);
                if (value.phone_info && value.phone_info.phone) {
                    setPhone(value.phone_info.phone);
                } else {
                    setPhone('');
                }
                if (value.email_info && value.email_info.email) {
                    setEmail(value.email_info.email);
                } else {
                    setEmail('');
                }
            } else {
                setName('');
                setPhone('');
                setEmail('');
            }
            setNameState(undefined);
            setPhoneState(undefined);
            setEmailState(undefined);
        }
    }, [visible]);

    const trimName = trim(name);
    const trimEmail = trim(email);
    const trimPhone = trim(phone);

    const doSave = () => {
        if (size(trimName) > TARGET_NAME || size(trimEmail) > TARGET_EMAIL) {
            return;
        }
        const data: Record<string, string> = {
            name: trimName,
            phone: trimPhone,
            email: trimEmail
        };
        let url = '';
        if (value) {
            // 修改
            data._id = value._id;
            url = getRequestUrl(API.message.update_receiver, authModel.tenantId);
        } else {
            // 添加
            url = getRequestUrl(API.message.create_receiver, authModel.tenantId);
        }
        const configErrorCodes = [
            ECode.USER_PHONE_FORMAT_INVALID,
            ECode.USER_PHONE_REQUIRED,
            ECode.MEMBER_PHONE_DUPLICATE,
            ECode.USER_EMAIL_HAS_BEEN_REGISTERED,
            ECode.USER_EMAIL_IS_INCORRECT,
            ECode.MESSAGE_RECEIVER_AT_LEAST_ONE_PHONE_EMAIL
        ];
        return post(url, data, {
            customHandleErrorCodes: configErrorCodes
        })
            .then((res) => {
                const sent = get(res, 'data.sent');
                showSentToast(sent);
                const receiver = get(res, 'data.receiver');
                onOk && onOk(receiver);
            })
            .catch((res) => {
                const err = get(res, 'response.data');
                if (configErrorCodes.includes(err.code)) {
                    setErrorState(err);
                }
            });
    };
    const showSentToast = (sent: any) => {
        let msg = '';
        if (sent) {
            if (sent.email && sent.phone) {
                msg = platformIntl.get('msg.validate.send.all');
            } else if (sent.email) {
                msg = platformIntl.get('msg.validate.send.email');
            } else if (sent.phone) {
                msg = platformIntl.get('msg.validate.send.phone');
            }
        }
        if (msg) {
            Message.add({
                messageType: 'toast',
                type: 'success',
                message: msg
            });
        }
    };
    const setErrorState = (err: any) => {
        switch (get(err, 'code')) {
            case ECode.USER_PHONE_FORMAT_INVALID:
            case ECode.USER_PHONE_REQUIRED:
            case ECode.MEMBER_PHONE_DUPLICATE:
                setPhoneState('error');
                Message.add({
                    messageType: 'toast',
                    type: 'warning',
                    message: getErrorMsg(err)
                });
                break;
            case ECode.USER_EMAIL_HAS_BEEN_REGISTERED:
            case ECode.USER_EMAIL_IS_INCORRECT:
                setEmailState('error');
                Message.add({
                    messageType: 'toast',
                    type: 'warning',
                    message: getErrorMsg(err)
                });
                break;
            case ECode.MESSAGE_RECEIVER_AT_LEAST_ONE_PHONE_EMAIL:
                if (isEmpty(trimName)) {
                    setNameState('error');
                } else {
                    if (isEmpty(trimEmail)) {
                        setEmailState('error');
                    }
                    if (isEmpty(trimPhone)) {
                        setPhoneState('error');
                    }
                }
                Message.add({
                    messageType: 'toast',
                    type: 'warning',
                    message: getErrorMsg(err)
                });
                break;
            default:
                Message.add({
                    messageType: 'toast',
                    type: 'error',
                    message: getErrorMsg(err)
                });
                break;
        }
    };
    const renderContent = () => {
        return (
            <>
                <div className="edit-item">
                    <label>{platformIntl.get('name')}</label>
                    <ErrorInput
                        style={{ width: '100%' }}
                        errMsg={
                            size(trimName) > TARGET_NAME
                                ? platformIntl.get('max.length.error.message', { max: TARGET_NAME })
                                : ''
                        }
                        className="edit-input"
                        value={name}
                        onChange={setName}
                        onFocus={() => setNameState(undefined)}
                        onBlur={() =>
                            size(trimName) > 0 ? setNameState(undefined) : setNameState('error')
                        }
                        inputState={size(trimName) > TARGET_NAME ? 'error' : nameState}
                    />
                </div>
                {hasFunctionContactPhone() && (
                    <div className="edit-item">
                        <label>{platformIntl.get('phone')}</label>
                        <Input
                            className="edit-input"
                            value={phone}
                            onChange={(val) => setPhone(val)}
                            onBlur={() => setPhoneState(undefined)}
                            inputState={phoneState}
                        />
                    </div>
                )}
                <div className="edit-item">
                    <label>{platformIntl.get('email')}</label>
                    <ErrorInput
                        style={{ width: '100%' }}
                        errMsg={
                            size(trimEmail) > TARGET_EMAIL
                                ? platformIntl.get('max.length.error.message', {
                                      max: TARGET_EMAIL
                                  })
                                : ''
                        }
                        className="edit-input"
                        value={email}
                        onChange={setEmail}
                        onFocus={() => setEmailState(undefined)}
                        inputState={size(trimEmail) > TARGET_EMAIL ? 'error' : emailState}
                    />
                </div>
            </>
        );
    };
    return (
        <Dialog
            className="fx-target-edit-pane"
            title={
                value ? platformIntl.get('msg.target.update') : platformIntl.get('msg.target.add')
            }
            visible={visible}
            height={346}
            onCancel={() => onCancel && onCancel()}
            onOk={doSave}
            text4Ok={platformIntl.get('save')}
            text4Cancel={platformIntl.get('cancel')}
        >
            {renderContent()}
        </Dialog>
    );
};

export default MsgTargetEditPane;
